import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Button, message, Modal } from "antd";
import {
  DownloadOutlined,
  CloseOutlined,
  ShareAltOutlined,
  PlusSquareOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { isPWAMode } from "@extensions";
import "./pwa-install-prompt.css";

const PwaInstallPrompt = () => {
  const company = useSelector((s) => s.company);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  // const showMessage = text => message.success(text);
  const showMessage = () => {};

  useEffect(() => {
    // Проверяем, запущено ли приложение в режиме PWA
    const isPWA = isPWAMode();
    setIsStandalone(isPWA);

    // ВАЖНО: Если уже в PWA режиме - не показываем промпт вообще
    if (isPWA) {
      showMessage("Уже в PWA режиме");
      return;
    }

    // ВАЖНО: Если нет pwaFilename - не показываем промпт установки
    if (!company || !company.pwaFileName) {
      showMessage("Нет pwaFilename");
      return;
    }

    // ВАЖНО: Проверяем, было ли приложение уже установлено ранее
    const wasInstalled = localStorage.getItem("pwa-app-installed");
    if (wasInstalled === "true") {
      showMessage("Приложение уже установлено");
      return;
    }

    // Определяем iOS устройство
    const userAgent = window.navigator.userAgent.toLowerCase();
    const iOS = /iphone|ipad|ipod/.test(userAgent);
    setIsIOS(iOS);

    // Проверяем, отклонял ли пользователь установку ранее
    const dismissed = localStorage.getItem("pwa-install-dismissed");
    const dismissedDate = dismissed ? new Date(dismissed) : null;
    const daysSinceDismissed = dismissedDate
      ? (new Date() - dismissedDate) / (1000 * 60 * 60 * 24)
      : null;

    // Показываем промпт снова, если прошло более 7 дней
    if (dismissedDate && daysSinceDismissed < 7) {
      showMessage("Промпт уже показывали менее 7 дней назад");
      return;
    }

    // Для iOS показываем инструкции, если не в standalone режиме
    if (iOS && company) {
      // Задержка, чтобы пользователь освоился
      const timer = setTimeout(() => {
        setShowPrompt(true);
        showMessage("Показываем промпт для iOS старт");
      }, 3000);
      showMessage("Показываем промпт для iOS");
      return () => clearTimeout(timer);
    }

    // Слушаем событие beforeinstallprompt (для Android/Desktop)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);

      // Показываем промпт, если компания загружена
      if (company) {
        setShowPrompt(true);
        showMessage("Показываем промпт для Android/Desktop");
      }
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    // Слушаем событие успешной установки приложения
    const handleAppInstalled = () => {
      console.log("PWA приложение успешно установлено");
      // Запоминаем установку в localStorage
      localStorage.setItem("pwa-app-installed", "true");
      localStorage.setItem("pwa-app-installed-date", new Date().toISOString());
      setShowPrompt(false);
      setIsStandalone(true);
      showMessage("PWA приложение успешно установлено");
    };

    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, [company]);

  // Эффект для показа промпта когда компания загрузится
  useEffect(() => {
    // НЕ показываем если уже в PWA режиме
    if (isPWAMode()) {
      setShowPrompt(false);
      showMessage("уже в PWA режиме");
      return;
    }

    // НЕ показываем если приложение уже было установлено ранее
    const wasInstalled = localStorage.getItem("pwa-app-installed");
    if (wasInstalled === "true") {
      setShowPrompt(false);
      showMessage("Приложение уже было установлено ранее");
      return;
    }

    // Если есть отложенный промпт и компания загружена, показываем
    if (deferredPrompt && company && !isStandalone) {
      setShowPrompt(true);
      showMessage("Показываем промпт");
    }
  }, [company, deferredPrompt, isStandalone]);

  // Функция для очистки PWA данных из localStorage
  const clearPWAData = () => {
    showMessage("Очистка");
    localStorage.removeItem("pwa-app-installed");
    localStorage.removeItem("pwa-install-dismissed");
    localStorage.removeItem("pwa-app-installed-date");
    message.success("PWA данные очищены из localStorage");
    console.log(
      "Очищены ключи: pwa-app-installed, pwa-install-dismissed, pwa-app-installed-date",
    );
  };

  const renderClear = () => {
    showMessage("Меню очистки");
    return (
      <div className="pwa-install-clear-wrapper">
        <Button
          type="link"
          size="small"
          icon={<DeleteOutlined />}
          onClick={clearPWAData}
          className="pwa-install-clear-button"
        >
          Очистить PWA
        </Button>
      </div>
    );
  };

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      showMessage("Нет отложенного промпта");
      return;
    }

    // Показываем браузерный промпт установки
    deferredPrompt.prompt();

    // Ждем ответа пользователя
    const { outcome } = await deferredPrompt.userChoice;

    console.log(`User response to the install prompt: ${outcome}`);

    // Если пользователь принял установку - запоминаем это
    if (outcome === "accepted") {
      console.log("Пользователь принял установку");
      localStorage.setItem("pwa-app-installed", "true");
      localStorage.setItem("pwa-app-installed-date", new Date().toISOString());
      showMessage("Пользователь принял установку");
    }

    // Очищаем deferred prompt
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    localStorage.setItem("pwa-install-dismissed", new Date().toISOString());
    setShowPrompt(false);
    showMessage("Очищаем deferred prompt");
  };

  // Проверка флага установки из localStorage
  const wasInstalled = localStorage.getItem("pwa-app-installed") === "true";

  // Не показываем, если:
  // - Уже в PWA режиме
  // - Приложение было установлено ранее (флаг в localStorage)
  // - Компания еще не загружена
  // - У компании нет pwaFileName (PWA не настроено)
  // - Промпт не должен показываться
  // - Для не-iOS нет deferredPrompt
  if (isPWAMode() || isStandalone || !company || !company.pwaFileName) {
    return null;
  }

  if (wasInstalled || !showPrompt || (!isIOS && !deferredPrompt))
    return renderClear();

  // Рендер инструкций для iOS
  const renderIOSInstructions = () => (
    <div className="pwa-install-content">
      <div className="pwa-install-icon">
        {company && company.pwaImage ? (
          <img
            src={company.pwaImage}
            alt="Иконка приложения"
          />
        ) : (
          <DownloadOutlined />
        )}
      </div>
      <h3>Установить {company?.name || "приложение"}</h3>
      <p>Установите наше приложение для быстрого доступа с домашнего экрана!</p>
      <div className="pwa-install-ios-instructions">
        <div className="pwa-install-step">
          <ShareAltOutlined />
          <span>
            1. Нажмите кнопку "Поделиться" <ShareAltOutlined /> внизу экрана
          </span>
        </div>
        <div className="pwa-install-step">
          <PlusSquareOutlined />
          <span>
            2. Выберите "На экран «Домой»" <PlusSquareOutlined />
          </span>
        </div>
        <div className="pwa-install-step">
          <DownloadOutlined />
          <span>3. Нажмите "Добавить"</span>
        </div>
      </div>
      <div className="pwa-install-actions">
        <Button
          icon={<CloseOutlined />}
          onClick={handleDismiss}
          size="large"
          block
        >
          Понятно
        </Button>
      </div>
    </div>
  );

  // Рендер для Android/Desktop
  const renderAndroidInstructions = () => (
    <div className="pwa-install-content">
      <div className="pwa-install-icon">
        {company && company.pwaImage ? (
          <img
            src={company.pwaImage}
            alt="Иконка приложения"
          />
        ) : (
          <DownloadOutlined />
        )}
      </div>
      <h3>Установить {company?.name || "приложение"}</h3>
      <p>
        Установите наше приложение для быстрого доступа и лучшего опыта
        использования. Оно будет доступно с вашего домашнего экрана!
      </p>
      <div className="pwa-install-actions">
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleInstallClick}
          size="large"
          style={
            company?.primaryColor
              ? {
                  backgroundColor: company.primaryColor,
                  borderColor: company.primaryColor,
                }
              : {}
          }
        >
          Установить
        </Button>
        <Button icon={<CloseOutlined />} onClick={handleDismiss} size="large">
          Не сейчас
        </Button>
      </div>
    </div>
  );

  showPrompt && showMessage("Отображение запроса");

  return showPrompt ? (
    <Modal
      open={true}
      onCancel={handleDismiss}
      footer={null}
      closable={false}
      centered
      className="pwa-install-modal"
      getContainer={() => document.body}
      zIndex={2147483647}
      maskClosable
      destroyOnClose
    >
      {isIOS ? renderIOSInstructions() : renderAndroidInstructions()}
    </Modal>
  ) : (
    renderClear()
  );
};

export default PwaInstallPrompt;
